import React from "react";
import styled, { keyframes } from "styled-components";
import { useLocation } from "react-router-dom";
import { RESUME_URL } from "../../constants";

const ResumeRibbon: React.FC = () => {
  const { pathname } = useLocation();
  if (pathname === "/experience") return null;

  return (
  <RibbonContainer>
    {/* Web thread from navbar */}
    <WebThread />
    <WebThreadShine />

    {/* Red ribbon with vertical text */}
    <Ribbon href={RESUME_URL} target="_blank" rel="noopener noreferrer">
      <RibbonBody>
        {"RESUME".split("").map((ch, i) => (
          <RibbonChar key={i}>{ch}</RibbonChar>
        ))}
        <RibbonArrow>↓</RibbonArrow>
      </RibbonBody>
      <RibbonTail />
    </Ribbon>
  </RibbonContainer>
  );
};

export default ResumeRibbon;

/* Animations */
const swing = keyframes`
  0%, 100% { transform: rotate(3deg); }
  25% { transform: rotate(-4deg); }
  50% { transform: rotate(2deg); }
  75% { transform: rotate(-3deg); }
`;

const threadShimmer = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: 0 30px; }
`;

/* Styled */
const RibbonContainer = styled.div`
  position: fixed;
  top: 60px; /* Below navbar */
  right: 8%;
  z-index: 999;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${swing} 4s ease-in-out infinite;
  transform-origin: top center;
  pointer-events: auto;

  @media (max-width: 768px) {
    right: 3%;
    transform: scale(0.8);
    transform-origin: top right;
  }
`;

const WebThread = styled.div`
  width: 1.5px;
  height: 40px;
  background: linear-gradient(180deg,
    rgba(200,200,200,0.5),
    rgba(200,200,200,0.2),
    rgba(200,200,200,0.4)
  );
  border-radius: 1px;
`;

const WebThreadShine = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 3px;
  height: 40px;
  background: repeating-linear-gradient(
    180deg,
    transparent,
    transparent 5px,
    rgba(255,255,255,0.06) 5px,
    rgba(255,255,255,0.06) 7px
  );
  animation: ${threadShimmer} 2s linear infinite;
  pointer-events: none;
`;

const Ribbon = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const RibbonBody = styled.div`
  background: linear-gradient(180deg, #cc1111, #aa0808, #cc1111);
  padding: 10px 8px 8px;
  border-radius: 3px 3px 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  box-shadow:
    2px 2px 8px rgba(0,0,0,0.4),
    inset 1px 0 3px rgba(255,100,100,0.1),
    inset -1px 0 3px rgba(0,0,0,0.2);
  position: relative;

  /* Fabric texture */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
      90deg,
      transparent,
      transparent 2px,
      rgba(0,0,0,0.03) 2px,
      rgba(0,0,0,0.03) 3px
    );
    border-radius: inherit;
    pointer-events: none;
  }

  /* Gold border lines */
  &::after {
    content: "";
    position: absolute;
    inset: 3px;
    border: 1px solid rgba(255,215,0,0.15);
    border-radius: 2px;
    pointer-events: none;
  }
`;

const RibbonChar = styled.span`
  font-family: "Cinzel", serif;
  font-size: 0.7rem;
  font-weight: 700;
  color: #ffd700;
  text-shadow: 0 1px 2px rgba(0,0,0,0.5);
  letter-spacing: 0.15em;
  line-height: 1;
`;

const RibbonArrow = styled.span`
  font-size: 0.6rem;
  color: rgba(255,215,0,0.6);
  margin-top: 2px;
`;

const RibbonTail = styled.div`
  width: 0;
  height: 0;
  border-left: 12px solid #aa0808;
  border-right: 12px solid #aa0808;
  border-bottom: 10px solid transparent;
  filter: drop-shadow(0 2px 3px rgba(0,0,0,0.3));
`;
